/**
 * CourseCardPlayful
 * Theme-first course card: clean rounded surface, subject glyph on the primary
 * tint, quiet meta row, AI tutor status pill, and a compact primary CTA.
 * Subject identity comes from the glyph and label only; color stays on-theme.
 */
import React from 'react';
import { CourseResponse } from '../../api/courses';
import { getCourseVisual } from '../../utils/courseVisuals';
import SubjectGlyph from './SubjectGlyph';

interface Props {
  course: CourseResponse;
  onClick?: () => void;
  ctaLabel?: string;
  enrolled?: boolean;
  /** Teacher actions — when provided, small round icon buttons appear top-right. */
  onEdit?: () => void;
  onDelete?: () => void;
  showActions?: boolean;
}

/** AI tutor readiness as a quiet status pill. */
const RagStatus: React.FC<{ rag?: CourseResponse['rag_status'] }> = ({ rag }) => {
  const pct = rag?.is_ready ? 100 : rag?.progress_percentage ?? 0;
  if (rag?.is_ready) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        AI tutor ready
      </span>
    );
  }
  if (pct > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
        Preparing {Math.round(pct)}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      No tutor yet
    </span>
  );
};

const CourseCardPlayful: React.FC<Props> = ({ course, onClick, ctaLabel = 'Start', enrolled, onEdit, onDelete, showActions }) => {
  const visual = getCourseVisual(course.title);

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      className="group flex w-full cursor-pointer flex-col rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/20 dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
    >
      {/* Header: subject glyph + subject label / actions */}
      <div className="flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
          <SubjectGlyph glyph={visual.glyph} className="h-6 w-6" strokeWidth={1.9} />
        </span>
        {showActions && (onEdit || onDelete) ? (
          <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            {onEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                title="Edit course"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-secondary-700 dark:hover:text-gray-200"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                title="Delete course"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            )}
          </div>
        ) : enrolled ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
            Enrolled
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
            {visual.label}
          </span>
        )}
      </div>

      {/* Title + description */}
      <h3 className="mt-4 line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">
        {course.title}
      </h3>
      <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-gray-500 dark:text-gray-400">
        {course.description || 'No description yet.'}
      </p>

      {/* Meta row */}
      <div className="mt-3 flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M10 9.5l5 2.5-5 2.5z" /></svg>
          {course.lecture_count} {course.lecture_count === 1 ? 'lecture' : 'lectures'}
        </span>
        {course.semester && course.year && (
          <span className="inline-flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2.5" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
            {course.semester} {course.year}
          </span>
        )}
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.2" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>
          <span className="truncate">{course.instructor_name || course.instructor}</span>
        </span>
      </div>

      {/* Footer: AI status + CTA */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-secondary-700">
        <RagStatus rag={course.rag_status} />
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all group-hover:bg-primary-700 group-active:translate-y-0.5 group-active:shadow-none">
          {ctaLabel}
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </span>
      </div>
    </div>
  );
};

export default CourseCardPlayful;
